import { create } from "zustand";
import { rebase } from "@/lib/mutators/apply";
import type { OutboxOp } from "@/lib/mutators/types";
import type { BucketKind } from "@/types";
import { todayIso } from "@/lib/dates";
import { dateInZone } from "@/lib/views/order";

// SyncTaskSerializer: a task as stored, derived fields are computed on the device.
export interface SyncTask {
  id: string;
  listId: string;
  parentId: string | null;
  title: string;
  notes: string | null;
  status: "pending" | "completed";
  position: string;
  myDayPosition: string | null;
  myDayOn: string | null;
  myDayDismissedOn: string | null;
  myDaySequentialOn: string | null;
  dueOn: string | null;
  recurrenceRule: string | null;
  reusable: boolean;
  visibleFrom: string | null;
  completedAt: string | null;
  lastCompletedAt: string | null;
  discardedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SyncList {
  id: string;
  name: string;
  kind: BucketKind;
  position: string;
  discardedAt: string | null;
}

export interface SyncMeta {
  cursor: number;
  // The clock value of a snapshot still being paged; null once the copy is whole.
  snapshotBase: number | null;
  today: string | null;
  timeOfDay: string | null;
  zone: string | null;
  userId: string | null;
}

export const EMPTY_META: SyncMeta = { cursor: 0, snapshotBase: null, today: null, timeOfDay: null, zone: null, userId: null };

// A task as kept in IndexedDB: the server time of the pull that brought it travels along.
export interface PersistedTask extends SyncTask {
  pulledAt?: string;
}

interface TaskStoreState {
  // The server's copy; pages render viewTasks(), which replays `pending` on top.
  tasks: Record<string, SyncTask>;
  lists: Record<string, SyncList>;
  meta: SyncMeta;
  ready: boolean;
  // Server time of the version held for each task id, tombstones included.
  pulledAt: Record<string, string>;
  // Outbox ops whose effects no pull has brought yet, oldest first.
  pending: OutboxOp[];
  // Pending ops up to this seq are on the server already (the service worker pushed them).
  ackedThrough: number;

  replaceAll: (tasks: SyncTask[], lists: SyncList[], meta: Partial<SyncMeta>, pulledAt?: string) => void;
  upsert: (tasks: SyncTask[], lists: SyncList[], meta?: Partial<SyncMeta>, pulledAt?: string) => void;
  // tombstones: server time of each task pulled as removed.
  loadPersisted: (tasks: PersistedTask[], lists: SyncList[], meta: Partial<SyncMeta>, tombstones?: Record<string, string>) => void;
  setReady: (ready: boolean) => void;
  setPending: (pending: OutboxOp[], ackedThrough?: number) => void;
  addPending: (op: OutboxOp) => void;
  clear: () => void;
}

// Server times are ISO 8601 with microseconds. Some engines reject more than three
// fraction digits in Date.parse, so the fraction is parsed apart.
export function serverMicros(iso: string): number {
  const fraction = /\.(\d+)/.exec(iso)?.[1] ?? "";
  const seconds = Date.parse(iso.replace(/\.\d+/, "")) / 1000;
  return seconds * 1_000_000 + Number(fraction.padEnd(6, "0").slice(0, 6));
}

// A discarded row arrives as a tombstone: it only ever means "drop it".
function mergeRows<T extends { id: string; discardedAt: string | null }>(
  into: Record<string, T>,
  rows: T[],
): Record<string, T> {
  const next = { ...into };
  for (const row of rows) {
    if (row.discardedAt) delete next[row.id];
    else next[row.id] = row;
  }
  return next;
}

function stamp(into: Record<string, string>, ids: string[], at: string | undefined): Record<string, string> {
  if (at === undefined || ids.length === 0) return into;
  const next = { ...into };
  for (const id of ids) next[id] = at;
  return next;
}

export const TOMBSTONE_TTL_MS = 60 * 60 * 1000;

function prunedTombstones(pulledAt: Record<string, string>, tasks: Record<string, SyncTask>, now: string | undefined): Record<string, string> {
  if (now === undefined) return pulledAt;
  const oldest = serverMicros(now) - TOMBSTONE_TTL_MS * 1000;
  return Object.fromEntries(Object.entries(pulledAt).filter(([id, at]) => tasks[id] || serverMicros(at) >= oldest));
}

// Stamps of rows pulled as removed: a reset must not forget them.
export function tombstoneStamps(state: { tasks: Record<string, SyncTask>; pulledAt: Record<string, string> }): Record<string, string> {
  return Object.fromEntries(Object.entries(state.pulledAt).filter(([id]) => !state.tasks[id]));
}

function stripPulledAt({ pulledAt: _pulledAt, ...task }: PersistedTask): SyncTask {
  return task;
}

export const useTaskStore = create<TaskStoreState>()((set) => ({
  tasks: {},
  lists: {},
  meta: EMPTY_META,
  ready: false,
  pulledAt: {},
  pending: [],
  ackedThrough: 0,

  replaceAll: (tasks, lists, meta, pulledAt) =>
    set((s) => {
      const nextTasks = mergeRows({}, tasks);
      const stamps = stamp(tombstoneStamps(s), tasks.map((t) => t.id), pulledAt);
      return {
        tasks: nextTasks,
        lists: mergeRows({}, lists),
        meta: { ...s.meta, ...meta },
        pulledAt: prunedTombstones(stamps, nextTasks, pulledAt),
      };
    }),

  upsert: (tasks, lists, meta = {}, pulledAt) =>
    set((s) => {
      const ids = tasks.map((t) => t.id);
      const nextTasks = mergeRows(s.tasks, tasks);
      return {
        tasks: nextTasks,
        lists: mergeRows(s.lists, lists),
        meta: { ...s.meta, ...meta },
        pulledAt: prunedTombstones(stamp(s.pulledAt, ids, pulledAt), nextTasks, pulledAt),
      };
    }),

  loadPersisted: (persisted, lists, meta, tombstones = {}) =>
    set((s) => {
      const tasks: Record<string, SyncTask> = {};
      const pulledAt: Record<string, string> = { ...tombstones };
      for (const row of persisted) {
        tasks[row.id] = stripPulledAt(row);
        if (row.pulledAt) pulledAt[row.id] = row.pulledAt;
      }
      return { tasks, lists: mergeRows({}, lists), meta: { ...s.meta, ...meta }, pulledAt };
    }),

  setReady: (ready) => set({ ready }),

  setPending: (pending, ackedThrough) => set(ackedThrough === undefined ? { pending } : { pending, ackedThrough }),

  addPending: (op) => set((s) => ({ pending: [...s.pending, op] })),

  clear: () => set({ tasks: {}, lists: {}, meta: EMPTY_META, ready: false, pulledAt: {}, pending: [], ackedThrough: 0 }),
}));

export const unsentAfter = <T extends { seq: number | null }>(ops: readonly T[], ackedThrough: number): T[] =>
  ops.filter((op) => op.seq === null || op.seq > ackedThrough);

// The changes still waiting to reach the server.
export function unsentCount({ pending, ackedThrough }: Pick<TaskStoreState, "pending" | "ackedThrough">): number {
  return unsentAfter(pending, ackedThrough).length;
}

type ViewInput = Pick<TaskStoreState, "tasks" | "lists" | "pending">;

let lastView: (ViewInput & { today: string; view: Record<string, SyncTask> }) | null = null;

// Every mounted view shares one rebase per store state and day.
export function viewTasks({ tasks, lists, pending }: ViewInput, today: string): Record<string, SyncTask> {
  const last = lastView;
  if (last && last.tasks === tasks && last.lists === lists && last.pending === pending && last.today === today) return last.view;
  const view = rebase(tasks, lists, pending, today);
  lastView = { tasks, lists, pending, today, view };
  return view;
}

// The user's date now, in the zone the last pull reported; the device's before the first pull.
export function userToday(): string {
  const { zone } = useTaskStore.getState().meta;
  return zone ? dateInZone(new Date().toISOString(), zone) : todayIso();
}
